import { useState, useEffect, useMemo } from "react"
import { useTranslation } from 'react-i18next'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ChevronDown, Eye, EyeOff } from "lucide-react"
import { cn } from "@/lib/utils"
import { auth, db, signOutUser } from "@/lib/firebase"
import { onAuthStateChanged } from "firebase/auth"
import { deleteDoc, doc } from 'firebase/firestore'
import { ArtistCentricOpsView, type AnalyticsLoadState } from '@/components/chat/ArtistCentricOpsView'
import { AuthModal } from '@/components/auth/AuthModal'

import { MCDONALDS_ENDPOINT, CHAT_ANALYTICS_ENDPOINT } from "@/config/endpoints"

// Allowed emails that can skip re-authentication
const ALLOWED_EMAILS = [
  'jari@getmoretats.com',
  'lotta@getmoretats.com',
  'keijo@getmoretats.com'
];

// Development mode flag - automatically enabled for allowed emails in dev mode
const isDevMode = import.meta.env.DEV;
const DEV_MODE_BYPASS = isDevMode; // Simple: just check if we're in dev mode

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Rolling window end = today (local), start = today minus `days` (local calendar). */
const getRollingLastMessageRange = (days: number): { start: string; end: string } => {
  const endDate = new Date()
  const startDate = new Date()
  startDate.setDate(endDate.getDate() - days)
  return {
    start: formatDateForInput(startDate),
    end: formatDateForInput(endDate),
  }
}

const LAST_MESSAGE_WITHIN_OPTIONS = [
  { value: '7', label: '1 week' },
  { value: '14', label: '2 weeks' },
  { value: '30', label: '1 month' },
  { value: '60', label: '2 months' },
  { value: '180', label: '6 months' },
  { value: 'all', label: 'All time' },
] as const

type LastMessageWithinPreset = (typeof LAST_MESSAGE_WITHIN_OPTIONS)[number]['value']

const DEFAULT_LAST_MESSAGE_PRESET: LastMessageWithinPreset = '30'

// New backend response interfaces
interface Message {
  senderType: 'user' | 'artist' | 'assistant';
  content: string;
  timestamp: string | { seconds: number; nanoseconds: number };
  isRead: boolean;
  notificationSent?: boolean;
}

interface Chat {
  chatId: string;
  userId: string;
  artistId: string;
  userUsername: string;
  userEmail: string;
  artistName: string;
  createdAt: string;
  lastActive: string;
  offerSentDate: string | null;
  offerAcceptedDate: string | null;
  appointmentDate: string | null;
  status: 'booked' | 'offer_accepted' | 'offer_sent' | 'active';
  waitingFor: 'waiting_user' | 'waiting_artist' | null;
  messageCount: number;
  bookingId: string | null;
  bookingPrice: number | null;
  messages: Message[];
  confidence: number;
  chatSummary: string;
  analysisSummary: string;
  fromCache: boolean;
  cachedAt?: string;
  aiBookingDetected?: boolean;
  aiBookingDate?: string | null;
  aiBookingPrice?: number | null;
  platformExitLikelihood?: number;
  /** Backend synthetic row: Time4Tats manual booking without a chats/ document */
  bookingOnly?: boolean;
}

/** Must match backend `SYNTHETIC_BOOKING_ONLY_CHAT_PREFIX` in chat_analytics_handler.py */
const BOOKING_ONLY_CHAT_PREFIX = 't4t_booking_only_'

interface AggregateStats {
  totalAcceptedOffers: number;
  aiDetectedBookings: number;
  totalBookings: number;
  averageTimeFromChatCreatedToOfferSent: {
    hours: number;
    days: number;
    sampleSize: number;
  } | null;
  averageTimeFromOfferAcceptedToAppointment: {
    hours: number;
    days: number;
    sampleSize: number;
  } | null;
  averageAcceptedOfferPrice: number | null;
  totalRevenue: number | null;
  bookingsWithPriceData: number;
}

interface CacheStats {
  hits: number;
  misses: number;
  hitRate: number;
}

interface ChatAnalyticsRequest {
  dateRangeStart?: string;
  dateRangeEnd?: string;
  dateRangeType: 'chat_created' | 'offer_accepted' | 'appointment_date' | 'last_message';
  statusFilter?: string;
  /** When true, backend AI maps Time4Tats / tattoo-event talk (any language) to event date 2026-05-09. See backend `chat_analytics_handler.TIME4TATS_EVENT_DATE_ISO` / `create_conversion_analysis_prompt`. */
  analyzeConversion: boolean;
  useCached?: boolean;
}

/** Same calendar day as backend `TIME4TATS_EVENT_DATE_ISO` (9 May 2026). */
const TIME4TATS_EVENT_DAY_ISO = '2026-05-09'

function calendarDayFromChatDate(s: string | null | undefined): string | null {
  if (s == null || s === '') return null
  const t = String(s).trim()
  const m = t.match(/^(\d{4}-\d{2}-\d{2})/)
  if (m) return m[1]
  const d = new Date(t)
  if (Number.isNaN(d.getTime())) return null
  return formatDateForInput(d)
}

function chatHasTime4TatsEventDayBooking(c: Chat): boolean {
  const appt = calendarDayFromChatDate(c.appointmentDate)
  const ai = calendarDayFromChatDate(c.aiBookingDate)
  return appt === TIME4TATS_EVENT_DAY_ISO || ai === TIME4TATS_EVENT_DAY_ISO
}

interface ChatAnalyticsResponse {
  success: boolean;
  chats: Chat[];
  totalChats: number;
  skippedChats: number;
  stats: AggregateStats;
  cacheStats: CacheStats;
  filters: any;
  timestamp: string;
  error?: string;
}

// Legacy booking analysis interface (for compatibility with existing components)
interface BookingAnalysis {
  chatId: string
  isBooked: boolean
  confidence: number
  bookedTime?: string | null
  bookingType?: 'custom' | 'flash' | 'unknown'
  summary?: string
  reasoning?: string
}

function normalizeStatusFilter(s: string | undefined): string {
  if (s == null || s === '' || s === 'all') return 'all'
  return s
}

function presetSpanDays(p: LastMessageWithinPreset): number {
  if (p === 'all') return Number.POSITIVE_INFINITY
  return Number(p)
}

/** Subset chats by rolling last-activity window (matches server last_message range). */
function filterChatsByLastMessagePreset(chats: Chat[], preset: LastMessageWithinPreset): Chat[] {
  if (preset === 'all') return chats.slice()
  const { start } = getRollingLastMessageRange(Number(preset))
  const [y, mo, d] = start.split('-').map(Number)
  const startBoundary = new Date(y, mo - 1, d, 0, 0, 0, 0)
  return chats.filter(c => {
    const la = new Date(c.lastActive)
    return !Number.isNaN(la.getTime()) && la >= startBoundary
  })
}

function buildBookingAnalysesFromChats(chats: Chat[]): Map<string, BookingAnalysis> {
  const legacyAnalyses = new Map<string, BookingAnalysis>()
  chats.forEach(chat => {
    legacyAnalyses.set(chat.chatId, {
      chatId: chat.chatId,
      isBooked: chat.status === 'booked',
      confidence: chat.confidence,
      bookedTime: chat.appointmentDate,
      summary: chat.chatSummary,
      reasoning: chat.analysisSummary,
    })
  })
  return legacyAnalyses
}

/** Headline stats from the current chat list (aligned with backend aggregate logic). */
function computeAggregateStatsFromChats(chats: Chat[]): AggregateStats {
  const chatToOfferHours: number[] = []
  const offerToApptHours: number[] = []
  const acceptedPrices: number[] = []
  let acceptedOffers = 0
  let aiBookings = 0

  for (const c of chats) {
    const created = c.createdAt ? new Date(c.createdAt) : null
    const offerSent = c.offerSentDate ? new Date(c.offerSentDate) : null
    const offerAccepted = c.offerAcceptedDate ? new Date(c.offerAcceptedDate) : null
    const appt = c.appointmentDate ? new Date(c.appointmentDate) : null

    if (created && offerSent && !Number.isNaN(created.getTime()) && !Number.isNaN(offerSent.getTime())) {
      chatToOfferHours.push((offerSent.getTime() - created.getTime()) / 3_600_000)
    }
    if (offerAccepted && appt && !Number.isNaN(offerAccepted.getTime()) && !Number.isNaN(appt.getTime())) {
      offerToApptHours.push((appt.getTime() - offerAccepted.getTime()) / 3_600_000)
    }
    if (offerAccepted && !Number.isNaN(offerAccepted.getTime())) {
      acceptedOffers += 1
      if (c.bookingPrice != null) acceptedPrices.push(c.bookingPrice)
    } else if (c.aiBookingDetected) {
      aiBookings += 1
      if (c.aiBookingPrice != null) acceptedPrices.push(c.aiBookingPrice)
    }
  }

  let avgChatHours: number | null = null
  let avgChatDays: number | null = null
  if (chatToOfferHours.length > 0) {
    const h = chatToOfferHours.reduce((a, b) => a + b, 0) / chatToOfferHours.length
    avgChatHours = Math.round(h * 100) / 100
    avgChatDays = Math.round((h / 24) * 100) / 100
  }

  let avgLeadHours: number | null = null
  let avgLeadDays: number | null = null
  if (offerToApptHours.length > 0) {
    const h = offerToApptHours.reduce((a, b) => a + b, 0) / offerToApptHours.length
    avgLeadHours = Math.round(h * 100) / 100
    avgLeadDays = Math.round((h / 24) * 100) / 100
  }

  let avgPrice: number | null = null
  let totalRev: number | null = null
  if (acceptedPrices.length > 0) {
    avgPrice = Math.round((acceptedPrices.reduce((a, b) => a + b, 0) / acceptedPrices.length) * 100) / 100
    totalRev = Math.round(acceptedPrices.reduce((a, b) => a + b, 0) * 100) / 100
  }

  return {
    totalAcceptedOffers: acceptedOffers,
    aiDetectedBookings: aiBookings,
    totalBookings: acceptedOffers + aiBookings,
    averageTimeFromChatCreatedToOfferSent:
      avgChatHours != null && avgChatDays != null
        ? { hours: avgChatHours, days: avgChatDays, sampleSize: chatToOfferHours.length }
        : null,
    averageTimeFromOfferAcceptedToAppointment:
      avgLeadHours != null && avgLeadDays != null
        ? { hours: avgLeadHours, days: avgLeadDays, sampleSize: offerToApptHours.length }
        : null,
    averageAcceptedOfferPrice: avgPrice,
    totalRevenue: totalRev,
    bookingsWithPriceData: acceptedPrices.length,
  }
}

const isAllowedEmail = (email: string | null | undefined): boolean => {
  if (!email) {
    console.log('[MacdPage] isAllowedEmail check: email is null/undefined')
    return false
  }
  const emailLower = email.toLowerCase()
  const isAllowed = ALLOWED_EMAILS.includes(emailLower)
  console.log('[MacdPage] isAllowedEmail check:', { 
    original: email, 
    lowercase: emailLower, 
    isAllowed,
    allowedList: ALLOWED_EMAILS 
  })
  return isAllowed
}

// Chat interface that matches what ChatGrid expects
// This is a local interface for type safety, imported from ChatGrid as needed
interface UserDetails {
  phoneNumber?: string;
  fcmToken?: string;
  iosNotificationsEnabled?: boolean;
  smsNotificationsEnabled?: boolean;
}

interface ArtistDetails {
  phoneNumber?: string;
  fcmToken?: string;
  notificationPermissionStatus?: string;
  smsNotificationsEnabled?: boolean;
}

interface LocalChat {
  id: string;
  userId: string;
  artistId: string;
  createdAt: { seconds: number; nanoseconds: number };
  lastMessageAt: { seconds: number; nanoseconds: number } | null;
  lastMessage: string | null;
  messages: Array<{
    senderType: 'user' | 'artist' | 'assistant';
    content: string;
    timestamp: string | { seconds: number; nanoseconds: number };
    isRead: boolean;
    notificationSent?: boolean;
  }>;
  status: string;
  artistName?: string;
  hasLead?: boolean;
  userInstagram?: string | null;
  userEmail?: string | null;
  artistDetails?: ArtistDetails;
  userDetails?: UserDetails;
  offerSentDate?: string | null;
  offerAcceptedDate?: string | null;
  appointmentDate?: string | null;
  bookingPrice?: number | null;
  aiBookingDetected?: boolean;
  aiBookingDate?: string | null;
  aiBookingPrice?: number | null;
  platformExitLikelihood?: number;
  bookingOnly?: boolean;
}

export default function GetMoretatsMacdonaldsPage() {
  const { t } = useTranslation()
  
  // State management
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [authChecked, setAuthChecked] = useState(false)
  const [showAdminPassword, setShowAdminPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [adminPassword, setAdminPassword] = useState('')

  const [isVerified, setIsVerified] = useState(false)
  const [chats, setChats] = useState<Chat[]>([])
  const [stats, setStats] = useState<AggregateStats | null>(null)
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null)
  
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  // Filter state — rolling “last message / activity” window (server-side)
  const [lastMessageWithin, setLastMessageWithin] = useState<LastMessageWithinPreset>(DEFAULT_LAST_MESSAGE_PRESET)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest')
  /** Client-only: show chats with platform booking or AI-analyzed date on Time4Tats day (2026-05-09). */
  const [time4tatsDayOnly, setTime4tatsDayOnly] = useState(false)

  // Legacy compatibility - for existing components
  const [bookingAnalyses, setBookingAnalyses] = useState<Map<string, BookingAnalysis>>(new Map())
  const [analyzingChatIds] = useState<Set<string>>(new Set())
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false)
  /** Tracks overlay + refresh icon: full = recompute on server; filter = cached / normal fetch. */
  const [activeAnalyticsLoadKind, setActiveAnalyticsLoadKind] = useState<AnalyticsLoadState>('idle')
  /** Widest successful fetch for this status scope — used to narrow the activity window without a round-trip. */
  const [analyticsSuperset, setAnalyticsSuperset] = useState<{
    chats: Chat[]
    lastMessageWithin: LastMessageWithinPreset
    statusFilter: string
  } | null>(null)

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false)

  const displayedChats = useMemo(() => {
    if (!time4tatsDayOnly) return chats
    return chats.filter(chatHasTime4TatsEventDayBooking)
  }, [chats, time4tatsDayOnly])

  const displayStats = useMemo(() => {
    if (!time4tatsDayOnly) return stats
    return computeAggregateStatsFromChats(displayedChats)
  }, [stats, time4tatsDayOnly, displayedChats])

  type FetchAnalyticsOverrides = Partial<ChatAnalyticsRequest> & {
    lastMessageWithin?: LastMessageWithinPreset
  }

  const applyNarrowerLastMessageIfPossible = (newPreset: LastMessageWithinPreset): boolean => {
    if (!analyticsSuperset) return false
    if (normalizeStatusFilter(statusFilter) !== analyticsSuperset.statusFilter) return false
    if (presetSpanDays(newPreset) > presetSpanDays(analyticsSuperset.lastMessageWithin)) return false

    setLastMessageWithin(newPreset)
    const filtered = filterChatsByLastMessagePreset(analyticsSuperset.chats, newPreset)
    setChats(filtered)
    setStats(computeAggregateStatsFromChats(filtered))
    setBookingAnalyses(buildBookingAnalysesFromChats(filtered))
    return true
  }

  // New function to fetch analytics from backend
  const fetchAnalytics = async (filters?: FetchAnalyticsOverrides) => {
    const loadKind: AnalyticsLoadState = filters?.useCached === false ? 'full_refresh' : 'filter_refresh'
    setActiveAnalyticsLoadKind(loadKind)
    setIsLoadingAnalytics(true)
    setError(null)
    
    try {
      // Wait a moment to ensure auth state is fully settled
      await new Promise(resolve => setTimeout(resolve, 100))
      
      const user = auth.currentUser
      if (!user) {
        throw new Error('Not authenticated')
      }

      const idToken = await user.getIdToken(true)
      
      const preset = filters?.lastMessageWithin ?? lastMessageWithin
      const rolling =
        preset !== 'all' ? getRollingLastMessageRange(Number(preset)) : null

      const mergedStatus =
        filters != null && 'statusFilter' in filters ? filters.statusFilter : statusFilter
      const requestBody: ChatAnalyticsRequest = {
        dateRangeType: 'last_message',
        dateRangeStart: rolling?.start,
        dateRangeEnd: rolling?.end,
        statusFilter:
          mergedStatus != null && mergedStatus !== '' && mergedStatus !== 'all'
            ? mergedStatus
            : undefined,
        analyzeConversion: true,
        // Normal loads use server cache; explicit refresh sets useCached: false
        useCached: filters?.useCached !== false,
      }
      
      console.log('📊 Fetching analytics with filters:', requestBody)
      
      const response = await fetch(CHAT_ANALYTICS_ENDPOINT, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody)
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.statusText}`)
      }
      
      const data: ChatAnalyticsResponse = await response.json()
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch analytics')
      }
      
      console.log('✅ Analytics loaded:', {
        totalChats: data.totalChats,
        skippedChats: data.skippedChats,
        cacheHitRate: data.cacheStats.hitRate
      })
      
      setChats(data.chats)
      setStats(data.stats)
      setCacheStats(data.cacheStats)
      setBookingAnalyses(buildBookingAnalysesFromChats(data.chats))
      setAnalyticsSuperset({
        chats: data.chats,
        lastMessageWithin: preset,
        statusFilter: normalizeStatusFilter(mergedStatus),
      })
      
    } catch (err) {
      console.error('Error fetching analytics:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics')
    } finally {
      setIsLoadingAnalytics(false)
      setActiveAnalyticsLoadKind('idle')
    }
  }

  // Check if user is already authenticated and fetch chats if verified
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('[MacdPage] Auth state changed:', {
        userEmail: user?.email,
        isAllowed: user ? isAllowedEmail(user.email) : false,
        isVerified: localStorage.getItem('mcdonalds_verified') === 'true'
      })
      
      setCurrentUser(user)
      setAuthChecked(true)

      // If user is authenticated with allowed email and verified, fetch chats
      if (user && isAllowedEmail(user.email) && localStorage.getItem('mcdonalds_verified') === 'true') {
        console.log('[MacdPage] User is verified, fetching analytics...')
        setIsVerified(true)
        try {
          await fetchAnalytics()
        } catch (err) {
          console.error('[MacdPage] Error during initial analytics fetch:', err)
          // Don't clear verification on analytics error - let them retry
          setError('Failed to load analytics. Use the refresh button to retry.')
        }
      } else if (user && !isAllowedEmail(user.email)) {
        // User is authenticated but not allowed
        console.warn('[MacdPage] User email not allowed:', user.email)
        await signOutUser()
        setCurrentUser(null)
      } else if (user && isAllowedEmail(user.email) && localStorage.getItem('mcdonalds_verified') !== 'true') {
        console.log('[MacdPage] User allowed but not verified yet')
      }
    })
    
    return () => unsubscribe()
  }, [])

  // Adapter function to convert new backend format to LocalChat format for compatibility
  const convertToLocalChatFormat = (chats: Chat[]): LocalChat[] => {
    console.log('📊 Converting chats:', { totalChats: chats.length, sample: chats[0] })
    return chats.map(chat => ({
      id: chat.chatId,
      userId: chat.userId || '',
      artistId: chat.artistId,
      createdAt: { 
        seconds: Math.floor(new Date(chat.createdAt).getTime() / 1000),
        nanoseconds: 0
      },
      lastMessageAt: chat.lastActive ? {
        seconds: Math.floor(new Date(chat.lastActive).getTime() / 1000),
        nanoseconds: 0
      } : null,
      lastMessage: chat.messages.length > 0 ? chat.messages[chat.messages.length - 1].content : null,
      messages: chat.messages,
      status: chat.waitingFor || chat.status,
      artistName: chat.artistName,
      hasLead: chat.status === 'offer_sent' || chat.status === 'offer_accepted' || chat.status === 'booked',
      userInstagram: chat.userUsername,
      userEmail: chat.userEmail,
      artistDetails: undefined,  // Not included in new backend response
      userDetails: undefined,     // Not included in new backend response
      offerSentDate: chat.offerSentDate,
      offerAcceptedDate: chat.offerAcceptedDate,
      appointmentDate: chat.appointmentDate,
      bookingPrice: chat.bookingPrice,
      aiBookingDetected: chat.aiBookingDetected,
      aiBookingDate: chat.aiBookingDate,
      aiBookingPrice: chat.aiBookingPrice,
      platformExitLikelihood: chat.platformExitLikelihood,
      bookingOnly: chat.bookingOnly === true,
    }))
  }


  const handleDeleteChat = async (chatId: string) => {
    try {
      if (chatId.startsWith(BOOKING_ONLY_CHAT_PREFIX)) {
        const bookingId = chatId.slice(BOOKING_ONLY_CHAT_PREFIX.length)
        await deleteDoc(doc(db, 'bookings', bookingId))
      } else {
        await deleteDoc(doc(db, 'chats', chatId))
      }
      setChats(prevChats => {
        const next = prevChats.filter(chat => chat.chatId !== chatId)
        setStats(computeAggregateStatsFromChats(next))
        return next
      })
      setAnalyticsSuperset(prev =>
        prev ? { ...prev, chats: prev.chats.filter(c => c.chatId !== chatId) } : prev
      )
      setBookingAnalyses(prev => {
        const next = new Map(prev)
        next.delete(chatId)
        return next
      })
    } catch (error) {
      console.error('Error deleting chat:', error)
      setError('Failed to delete chat')
    }
  }

  // Handle toy key verification
  const handleVerifyToyKey = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!currentUser) {
      setError('Not authenticated')
      return
    }

    setLoading(true)

    try {
      // In dev mode, bypass backend verification
      if (DEV_MODE_BYPASS) {
        console.log('🚀 DEV MODE: Bypassing backend verification')
        setIsVerified(true)
        localStorage.setItem('mcdonalds_verified', 'true')
        await fetchAnalytics()
        return
      }

      // Production: require password and verify with backend
      if (!adminPassword) {
        setError('Toy key is required')
        return
      }

      const idToken = await currentUser.getIdToken(true)

      // Verify with backend
      const response = await fetch(MCDONALDS_ENDPOINT, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          password: adminPassword,
          email: currentUser.email
        })
      })

      const data = await response.json()

      if (!response.ok) {
        console.error('Server response:', data)
        throw new Error(data.error || `Verification failed: ${response.status} ${response.statusText}`)
      }

      if (!data.verified) {
        setError(data.message || 'Toy key verification failed')
        return
      }

      // After successful verification, fetch analytics
      setIsVerified(true)
      localStorage.setItem('mcdonalds_verified', 'true')
      await fetchAnalytics()
    } catch (error: any) {
      console.error('Error in verification:', error)
      setError(error.message || 'An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Placeholder for single chat analysis (no longer needed - backend does it all)
  const analyzeSingleChat = async (chatId: string) => {
    console.log('Single chat analysis is now handled by backend for chat:', chatId)
  }


  // Render chats list if verified
  if (isVerified) {
    // Show loading state while fetching analytics (only on initial load)
    if (isLoadingAnalytics && chats.length === 0 && !error) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="mb-4">
              <p className="text-lg font-semibold text-foreground mb-2">📊 Loading Chat Analytics</p>
              <p className="text-sm text-muted-foreground mb-4">Fetching and analyzing chats from backend...</p>
              <div className="flex justify-center gap-2 mb-6">
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
              {currentUser && (
                <p className="text-xs text-muted-foreground">
                  Logged in as {currentUser.email}
                </p>
              )}
            </div>
          </div>
        </div>
      )
    }

    // Show error state if analytics failed to load
    if (error && chats.length === 0) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="mb-6">
              <p className="text-lg font-semibold text-destructive mb-2">❌ Failed to Load Analytics</p>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              {currentUser && (
                <p className="text-xs text-muted-foreground mb-4">
                  Logged in as {currentUser.email}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  setError(null)
                  fetchAnalytics()
                }}
                disabled={isLoadingAnalytics}
                className="w-full"
              >
                {isLoadingAnalytics ? '⏳ Loading...' : '🔄 Retry'}
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  localStorage.removeItem('mcdonalds_verified')
                  await signOutUser()
                  setCurrentUser(null)
                  setIsVerified(false)
                  window.location.reload()
                }}
                className="w-full"
              >
                Sign Out and Start Over
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="flex min-h-screen flex-col bg-background lg:h-dvh lg:min-h-0 lg:overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-6 lg:p-8 w-full lg:min-h-0 lg:overflow-hidden">
          <div className="shrink-0 space-y-3">
          {/* Header: title + email on one line; metrics share remaining width evenly */}
          <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="flex min-w-0 max-w-full shrink-0 flex-nowrap items-baseline gap-x-2.5 sm:gap-x-3 lg:max-w-[min(32rem,42%)]">
              <h1 className="shrink-0 text-2xl font-bold tracking-tight whitespace-nowrap sm:text-3xl">
                Chat Analytics
              </h1>
              {currentUser && (
                <span
                  className="min-w-0 truncate text-xs text-muted-foreground sm:max-w-[18rem] md:max-w-[22rem] lg:max-w-[26rem]"
                  title={currentUser.email}
                >
                  {currentUser.email}
                </span>
              )}
            </div>

            {displayStats && (
              <div className="flex min-w-0 flex-1 flex-wrap gap-2 md:flex-nowrap md:items-stretch">
                <div className="min-w-0 flex-1 basis-[calc(50%-0.25rem)] rounded-md border border-border bg-card px-3 py-2 sm:basis-[calc(33.333%-0.334rem)] md:basis-0">
                  <h3 className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Bookings</h3>
                  <p className="text-lg font-bold tabular-nums leading-tight sm:text-xl">{displayStats.totalBookings ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">
                    {displayStats.totalAcceptedOffers ?? 0} + {displayStats.aiDetectedBookings ?? 0} AI
                  </p>
                </div>

                <div className="min-w-0 flex-1 basis-[calc(50%-0.25rem)] rounded-md border border-border bg-card px-3 py-2 sm:basis-[calc(33.333%-0.334rem)] md:basis-0">
                  <h3 className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Avg to offer</h3>
                  <p className="text-lg font-bold tabular-nums leading-tight sm:text-xl">
                    {displayStats.averageTimeFromChatCreatedToOfferSent?.days != null
                      ? `${displayStats.averageTimeFromChatCreatedToOfferSent.days.toFixed(1)}d`
                      : '—'}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight truncate">
                    {displayStats.averageTimeFromChatCreatedToOfferSent?.hours != null
                      ? `${displayStats.averageTimeFromChatCreatedToOfferSent.hours.toFixed(0)}h`
                      : 'No data'}
                  </p>
                </div>

                <div className="min-w-0 flex-1 basis-[calc(50%-0.25rem)] rounded-md border border-border bg-card px-3 py-2 sm:basis-[calc(33.333%-0.334rem)] md:basis-0">
                  <h3 className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Avg lead</h3>
                  <p className="text-lg font-bold tabular-nums leading-tight sm:text-xl">
                    {displayStats.averageTimeFromOfferAcceptedToAppointment?.days != null
                      ? `${displayStats.averageTimeFromOfferAcceptedToAppointment.days.toFixed(1)}d`
                      : '—'}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight">Accept → appt</p>
                </div>

                <div className="min-w-0 flex-1 basis-[calc(50%-0.25rem)] rounded-md border border-border bg-card px-3 py-2 sm:basis-[calc(33.333%-0.334rem)] md:basis-0">
                  <h3 className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Avg price</h3>
                  <p className="text-lg font-bold tabular-nums leading-tight sm:text-xl">
                    {displayStats.averageAcceptedOfferPrice != null ? `€${displayStats.averageAcceptedOfferPrice.toFixed(0)}` : '—'}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight">Bookings</p>
                </div>

                <div className="min-w-0 flex-1 basis-[calc(50%-0.25rem)] rounded-md border border-border bg-card px-3 py-2 sm:basis-[calc(33.333%-0.334rem)] md:basis-0">
                  <h3 className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Revenue</h3>
                  <p className="text-lg font-bold tabular-nums leading-tight sm:text-xl">
                    {displayStats.totalRevenue != null ? `€${displayStats.totalRevenue.toFixed(0)}` : '—'}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight">{displayStats.bookingsWithPriceData ?? 0} priced</p>
                </div>

                {cacheStats && (
                  <div className="min-w-0 flex-1 basis-[calc(50%-0.25rem)] rounded-md border border-border bg-card px-3 py-2 sm:basis-[calc(33.333%-0.334rem)] md:basis-0">
                    <h3 className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Cache</h3>
                    <p className="text-lg font-bold tabular-nums leading-tight sm:text-xl">
                      {cacheStats.hitRate != null ? `${cacheStats.hitRate.toFixed(0)}%` : '—'}
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-tight">
                      {cacheStats.hits ?? 0}/{cacheStats.misses ?? 0}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-3 p-4 rounded-lg border border-destructive/50 bg-destructive/10">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive mb-1">Error</p>
                  <p className="text-sm text-foreground">{error}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setError(null)}
                  className="shrink-0"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          {/* Filters (collapsible) */}
          <div className="mb-3 bg-card border border-border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setFiltersExpanded((v) => !v)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
              aria-expanded={filtersExpanded}
            >
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200',
                  filtersExpanded && 'rotate-180'
                )}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold leading-tight">Filters</h2>
                {!filtersExpanded && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    Last activity:{' '}
                    {LAST_MESSAGE_WITHIN_OPTIONS.find((o) => o.value === lastMessageWithin)?.label ?? lastMessageWithin}
                    {statusFilter !== 'all' && ` · ${statusFilter.replace(/_/g, ' ')}`}
                    {` · ${sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}`}
                    {time4tatsDayOnly && " · Time4Tats day (9 May '26)"}
                  </p>
                )}
              </div>
            </button>
            {filtersExpanded && (
            <div className="px-4 pb-4 pt-0 border-t border-border/60">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Last message within (rolling from today)
                </label>
                <select
                  value={lastMessageWithin}
                  onChange={(e) => {
                    const v = e.target.value as LastMessageWithinPreset
                    if (applyNarrowerLastMessageIfPossible(v)) return
                    setLastMessageWithin(v)
                    fetchAnalytics({ lastMessageWithin: v })
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-0"
                >
                  {LAST_MESSAGE_WITHIN_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Uses last chat activity (or last message time). All time loads every chat (slower).
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">Status</label>
                <select 
                  value={statusFilter} 
                  onChange={(e) => {
                    const v = e.target.value
                    setStatusFilter(v)
                    fetchAnalytics({ statusFilter: v })
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-0"
                >
                  <option value="all">All Statuses</option>
                  <option value="booked">Booked</option>
                  <option value="offer_accepted">Offer accepted</option>
                  <option value="offer_sent">Offer sent</option>
                  <option value="active">Active</option>
                  <option value="waiting_user">Waiting for User</option>
                  <option value="waiting_artist">Waiting for Artist</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">Sort chats</label>
                <select 
                  value={sortOrder} 
                  onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-0"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>


            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setTime4tatsDayOnly((v) => !v)}
                className={cn(
                  'relative z-0 inline-flex min-h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors text-filter-pill',
                  time4tatsDayOnly ? 'time4tats-btn' : 'time4tats-btn-inactive'
                )}
                title="Show only chats with a booking (or AI-inferred event booking) on 9 May 2026 — Time4Tats convention day."
              >
                <span>time4tats</span>
                <span className="text-[10px] font-medium opacity-90">9 May &apos;26</span>
              </button>
              <Button
                onClick={() => {
                  setLastMessageWithin(DEFAULT_LAST_MESSAGE_PRESET)
                  setStatusFilter('all')
                  setSortOrder('newest')
                  setTime4tatsDayOnly(false)
                  fetchAnalytics({
                    lastMessageWithin: DEFAULT_LAST_MESSAGE_PRESET,
                    statusFilter: 'all',
                  })
                }}
                variant="outline"
                disabled={isLoadingAnalytics}
                className="flex-1 sm:flex-none"
              >
                Reset filters
              </Button>
              <Button
                type="button"
                onClick={() => fetchAnalytics({ useCached: false })}
                disabled={isLoadingAnalytics}
                className="flex-1 sm:flex-none"
                title="Runs AI conversion analysis on every chat again; ignores stored cached results (slower)."
              >
                Re-analyze all (no cache)
              </Button>
            </div>
            </div>
            )}

          </div>

          </div>

          <div className="mt-2 flex min-h-0 flex-1 flex-col lg:min-h-0 lg:overflow-hidden">
            <ArtistCentricOpsView
              chats={convertToLocalChatFormat(displayedChats)}
              statusFilter={statusFilter}
              sortOrder={sortOrder}
              onDeleteChat={handleDeleteChat}
              bookingAnalyses={bookingAnalyses}
              analyzingChatIds={analyzingChatIds}
              onAnalyzeSingleChat={analyzeSingleChat}
              analyticsLoadState={
                isLoadingAnalytics
                  ? activeAnalyticsLoadKind
                  : 'idle'
              }
            />
          </div>

        </div>
      </div>
    );
  }

  // Check if we should show loading state while checking auth
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // Debug logging for auth state
  console.log('[MacdPage] Render state:', {
    authChecked,
    hasCurrentUser: !!currentUser,
    currentUserEmail: currentUser?.email,
    isEmailAllowed: currentUser ? isAllowedEmail(currentUser.email) : false,
    isVerified,
    allowedEmails: ALLOWED_EMAILS
  })

  // If user is not authenticated, show sign-in prompt
  if (!currentUser) {
    return (
      <>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="w-full max-w-md mx-auto p-4 sm:p-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">
                GetMoreTats Chat Analytics
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Sign in to access chat analytics
              </p>
              
              <Button
                onClick={() => setShowAuthModal(true)}
                className="w-full mb-4"
                size="lg"
              >
                Sign In
              </Button>

              <div className="mt-6 text-xs text-muted-foreground">
                <details>
                  <summary className="cursor-pointer hover:text-foreground">
                    Only authorized accounts can access this page
                  </summary>
                  <ul className="mt-2 space-y-1 font-mono bg-muted/50 p-2 rounded text-left">
                    {ALLOWED_EMAILS.map(email => (
                      <li key={email}>• {email}</li>
                    ))}
                  </ul>
                </details>
              </div>
            </div>
          </div>
        </div>

        <AuthModal
          isOpen={showAuthModal}
          onOpenChange={setShowAuthModal}
          defaultMode="login"
          source="macd-page"
          onSuccess={() => {
            setShowAuthModal(false)
          }}
        />
      </>
    )
  }

  // If user is authenticated but not allowed
  if (!isAllowedEmail(currentUser.email)) {
    return (
      <>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="w-full max-w-md mx-auto p-4 sm:p-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Access Denied
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Your account does not have access to this page
              </p>
              <div className="mb-6 space-y-2">
                <p className="text-sm text-muted-foreground">
                  Currently logged in as:
                </p>
                <p className="text-sm font-mono bg-muted px-3 py-2 rounded">
                  {currentUser.email || 'No email found'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Email allowed: {isAllowedEmail(currentUser.email) ? '✅ Yes' : '❌ No'}
                </p>
              </div>
              
              <Button
                onClick={async () => {
                  await signOutUser()
                  setCurrentUser(null)
                  localStorage.removeItem('mcdonalds_verified')
                  setShowAuthModal(true)
                }}
                className="w-full"
              >
                Sign Out & Sign In With Different Account
              </Button>
            </div>

            <div className="text-sm text-destructive bg-destructive/10 p-4 rounded-lg mb-4">
              <p className="font-medium mb-2">⚠️ Access Restricted</p>
              <p className="text-xs text-foreground/80 mb-3">
                Only authorized email addresses can access chat analytics. 
                Please sign out and sign in with an authorized account.
              </p>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Show authorized emails
                </summary>
                <ul className="mt-2 space-y-1 font-mono bg-background/50 p-2 rounded">
                  {ALLOWED_EMAILS.map(email => (
                    <li key={email}>• {email}</li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
        </div>

        <AuthModal
          isOpen={showAuthModal}
          onOpenChange={setShowAuthModal}
          defaultMode="login"
          source="macd-page-denied"
          onSuccess={() => {
            setShowAuthModal(false)
          }}
        />
      </>
    )
  }

  // User is authenticated with allowed email - show toy key verification form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-full max-w-md mx-auto p-4 sm:p-6">
        <div className="w-full">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              GetMoreTats Access
            </h2>
            <p className="text-sm text-muted-foreground">
              Logged in as {currentUser.email}
            </p>
            <div className="flex gap-3">
              <Button
                variant="link"
                className="p-0 h-auto text-xs"
                onClick={async () => {
                  await signOutUser()
                  setCurrentUser(null)
                  localStorage.removeItem('mcdonalds_verified')
                }}
              >
                Sign out
              </Button>
              {localStorage.getItem('mcdonalds_verified') === 'true' && (
                <Button
                  variant="link"
                  className="p-0 h-auto text-xs text-amber-600"
                  onClick={() => {
                    localStorage.removeItem('mcdonalds_verified')
                    setIsVerified(false)
                    setError(null)
                    window.location.reload()
                  }}
                >
                  Reset verification
                </Button>
              )}
            </div>
          </div>

          {DEV_MODE_BYPASS && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm font-medium text-amber-800">
                🚀 DEV MODE: Toy key verification is disabled
              </p>
            </div>
          )}

          <form onSubmit={handleVerifyToyKey} className="space-y-4">
            {/* Toy Key Field - Skip in dev mode */}
            {!DEV_MODE_BYPASS && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Toy Key
                </label>
                <div className="relative">
                  <Input
                    type={showAdminPassword ? "text" : "password"}
                    value={adminPassword}
                    onChange={(e) => {
                      setAdminPassword(e.target.value)
                      if (error) setError(null)
                    }}
                    placeholder="Enter toy key"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                    onClick={() => setShowAdminPassword(!showAdminPassword)}
                  >
                    {showAdminPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className={cn(
                "text-sm p-3 rounded-[var(--card-radius)]",
                error?.includes('Access granted') 
                  ? "text-green-600 bg-green-50"
                  : "text-destructive bg-destructive/10"
              )}>
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
              size="lg"
              disabled={loading}
            >
              {loading 
                ? t('common.loading')
                : DEV_MODE_BYPASS ? '🚀 Enter (DEV)' : 'Redeem Toy'
              }
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
